'use client';

/**
 * Per-holding research one-pager — used by Research Desk's
 * Portfolio / Alpha Zone / New Ideas tabs. Each card explains why
 * a name scores how it does, in the language of the composite
 * formula we ship.
 *
 * Pure compose-from-cron output (no per-card LLM). Sub-pieces:
 *   _holdings-research/types.ts      — shared interfaces + formatters
 *   _holdings-research/headline.ts   — one-line tag-line builder
 *   _holdings-research/card-parts.tsx — AxisStat + PillarBar
 *   _holdings-research/thesis-prose.tsx — templated thesis section
 */

import { useMemo, useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { compositeTier } from '@/lib/composite-tiers';
import {
  VALUATION_BETA,
  VALUATION_SHIFT,
  QUALITY_SCALE,
  prodQuality,
} from '@/lib/composite-score';
import { factorLabel, QUALITY_FACTORS, VALUATION_FACTORS } from '@/lib/clientScore';
import { DEFAULT_PILLAR_WEIGHTS } from '@/lib/factor-config';
import type {
  VelocityRow,
  TargetPriceRow,
  HedgeSignal,
  Stock,
  SelfPctileEntry,
} from './_holdings-research/types';
import { fmtZ, fmtPctile } from './_holdings-research/types';
import { buildHeadline } from './_holdings-research/headline';
import { AxisStat, PillarBar } from './_holdings-research/card-parts';
import { ThesisProse } from './_holdings-research/thesis-prose';

interface Props {
  symbols: string[];
  /** Optional per-symbol subtitle (sector default). */
  subtitleBySymbol?: Record<string, string>;
  /** Header title. */
  title: string;
  /** When set, replaces the default "Exit trigger" footer with custom copy. */
  actionPromptBySymbol?: Record<string, string>;
  /** Optional weights to render (basket weights or portfolio weights). */
  weightBySymbol?: Record<string, number>;
  /** Force the inner cards into a single column (instead of the default
   *  responsive 1-col / lg:2-col grid). Used by StockStoryDrawer so a
   *  single-symbol card occupies the full available column width without
   *  squishing labels into 25% of the drawer. */
  singleColumn?: boolean;
}

export function HoldingsResearchPanel({
  symbols,
  subtitleBySymbol,
  title,
  actionPromptBySymbol,
  weightBySymbol,
  singleColumn = false,
}: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: scores } = useResource<{ stocks: Stock[] }>('/api/stocks');
  const { data: vel } = useResource<{ rows: VelocityRow[] }>('/api/score-velocity');
  const { data: tp } = useResource<{ stocks: TargetPriceRow[] }>('/api/target-prices');
  const { data: hedge } = useResource<{ signals?: Record<string, HedgeSignal> }>('/api/hedge-signals');
  const { data: selfPctile } = useResource<{ bySymbol?: Record<string, SelfPctileEntry> }>('/api/self-percentiles');

  const stockBy = useMemo(() => {
    const m = new Map<string, Stock>();
    for (const s of scores?.stocks ?? []) m.set(s.symbol, s);
    return m;
  }, [scores]);

  const velBy = useMemo(() => {
    const m = new Map<string, VelocityRow>();
    for (const r of vel?.rows ?? []) m.set(r.symbol, r);
    return m;
  }, [vel]);

  const tpBy = useMemo(() => {
    const m = new Map<string, TargetPriceRow>();
    for (const r of tp?.stocks ?? []) m.set(r.symbol, r);
    return m;
  }, [tp]);

  if (!scores) {
    return <div className="text-[12px] text-[#64748b]">Loading research…</div>;
  }

  const ordered = [...symbols].sort((a, b) => {
    const ca = stockBy.get(a)?.compositeScore ?? velBy.get(a)?.composite ?? -99;
    const cb = stockBy.get(b)?.compositeScore ?? velBy.get(b)?.composite ?? -99;
    return cb - ca;
  });

  return (
    <section className="mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">{title}</h2>
        <span className="text-[10px] text-[#64748b]">{symbols.length} holdings · sorted by composite</span>
      </div>
      <div className={singleColumn ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-1 lg:grid-cols-2 gap-3'}>
        {ordered.map((sym) => (
          <ResearchCard
            key={sym}
            sym={sym}
            stock={stockBy.get(sym)}
            vel={velBy.get(sym)}
            target={tpBy.get(sym)}
            hedge={hedge?.signals?.[sym]}
            selfPctile={selfPctile?.bySymbol?.[sym]}
            isOpen={expanded === sym}
            onToggle={() => setExpanded(expanded === sym ? null : sym)}
            subtitle={subtitleBySymbol?.[sym]}
            weight={weightBySymbol?.[sym]}
            actionPrompt={actionPromptBySymbol?.[sym]}
          />
        ))}
      </div>
    </section>
  );
}

interface CardProps {
  sym: string;
  stock: Stock | undefined;
  vel: VelocityRow | undefined;
  target: TargetPriceRow | undefined;
  hedge: HedgeSignal | undefined;
  selfPctile: SelfPctileEntry | undefined;
  isOpen: boolean;
  onToggle: () => void;
  subtitle?: string;
  weight?: number;
  actionPrompt?: string;
}

function ResearchCard({
  sym, stock, vel, target, hedge, selfPctile,
  isOpen, onToggle, subtitle, weight, actionPrompt,
}: CardProps) {
  // Q + V are the SAME axes the production composite uses: blended-preferring.
  const Q = prodQuality(stock ?? {}) ?? vel?.q ?? null;
  const V = stock?.valuationScoreBlended ?? stock?.valuationScore ?? vel?.v ?? null;
  const C = stock?.compositeScore ?? vel?.composite ?? null;
  const tier = compositeTier(C);

  // Share of the composite from each axis, computed on the SAME α/β-scaled
  // axes the composite uses (abs of the scaled magnitudes).
  const mix = useMemo(() => {
    if (Q == null || V == null) return null;
    const qScaled = QUALITY_SCALE * Q;
    const vScaled = VALUATION_BETA * (V + VALUATION_SHIFT);
    const denom = Math.abs(qScaled) + Math.abs(vScaled);
    if (!(denom > 0)) return null;
    return { qShare: Math.abs(qScaled) / denom, vShare: Math.abs(vScaled) / denom };
  }, [Q, V]);

  const pillars = stock?.pillarScores;
  const pillarRows = [
    { key: 'profitability' as const, label: 'Profitability', weight: DEFAULT_PILLAR_WEIGHTS.profitability },
    { key: 'growth' as const,        label: 'Growth',        weight: DEFAULT_PILLAR_WEIGHTS.growth },
    { key: 'safety' as const,        label: 'Safety',        weight: DEFAULT_PILLAR_WEIGHTS.safety },
    { key: 'shareholderReturns' as const, label: 'Payout',   weight: DEFAULT_PILLAR_WEIGHTS.shareholderReturns },
  ];

  const factors = stock?.factors;
  const topFactors = useMemo(() => {
    if (!factors) return [];
    const qualityKeys = Object.values(QUALITY_FACTORS).flat().map(f => f.key);
    const valuationKeys = VALUATION_FACTORS.map(f => f.key);
    const keys = [...qualityKeys, ...valuationKeys];
    const rows = keys
      .map(k => ({ key: k as string, value: factors[k as string] ?? null }))
      .filter(r => r.value != null && Number.isFinite(r.value) && (r.value as number) > 0)
      .sort((a, b) => (b.value as number) - (a.value as number));
    return rows.slice(0, 5);
  }, [factors]);

  const px = target?.currentPrice ?? stock?.price ?? null;
  const fiveYr = target?.fiveYearTarget ?? null;
  const fiveYrAnn = fiveYr != null && px != null && px > 0
    ? (Math.pow(fiveYr / px, 1 / 5) - 1) * 100
    : null;
  const streetUpside = target?.analystTarget != null && px != null && px > 0
    ? (target.analystTarget / px - 1) * 100
    : null;
  const d20 = vel?.d20?.dcomp ?? null;

  const directionArrow = d20 == null ? '·' : d20 > 0.15 ? '▲' : d20 < -0.15 ? '▼' : '→';
  const directionColor = d20 == null ? 'text-[#64748b]'
    : d20 > 0.15 ? 'text-emerald-400'
    : d20 < -0.15 ? 'text-red-400'
    : 'text-[#94a3b8]';

  const subtitleText = subtitle ?? stock?.sector ?? '';

  const headline = buildHeadline({
    Q, V,
    d20,
    cPctile: selfPctile?.composite?.percentile ?? null,
    hedgeActive: !!hedge,
    alphaStreak: vel?.alphaStreak ?? null,
  });

  return (
    <div className={`rounded-md border bg-[#0d1117] overflow-hidden transition-colors ${isOpen ? 'border-amber-500/40' : 'border-[#1e293b] hover:border-[#334155]'}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-baseline justify-between px-3 py-2 gap-2 text-left"
      >
        <div className="flex items-baseline gap-3 flex-1 min-w-0">
          <span className={`font-mono font-bold text-[14px] ${tier.textClass}`}>{sym}</span>
          <span className="text-[11px] text-[#cbd5e1] truncate">{stock?.name ?? ''}</span>
          <span className="text-[10px] text-[#64748b] truncate">{subtitleText}</span>
        </div>
        <div className="flex items-baseline gap-2 shrink-0">
          {weight != null && (
            <span className="text-[10px] text-[#94a3b8] font-mono tabular-nums">wt {(weight * 100).toFixed(1)}%</span>
          )}
          {hedge && (
            <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40">🛡 hedge</span>
          )}
          <span className={`text-[9px] uppercase tracking-wide font-semibold ${tier.textClass}`}>{tier.label}</span>
        </div>
      </button>

      {headline && (
        <div className="px-3 pb-1.5 text-[11px] text-[#cbd5e1] italic">
          {headline}
        </div>
      )}

      <div className="px-3 pb-2 grid grid-cols-[1fr_1fr_1fr_auto] gap-3 text-[10px] items-baseline">
        <div>
          <div className="text-[9px] text-[#64748b] uppercase tracking-wide">Composite</div>
          <div className={`tabular-nums font-bold text-[15px] ${tier.textClass}`}>{C == null ? '—' : C.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[9px] text-[#64748b] uppercase tracking-wide">vs own hist</div>
          <div className="tabular-nums text-[#cbd5e1]">{fmtPctile(selfPctile?.composite?.percentile)}</div>
        </div>
        <div>
          <div className="text-[9px] text-[#64748b] uppercase tracking-wide">20d Δ</div>
          <div className={`tabular-nums ${directionColor}`}>{directionArrow} {fmtZ(d20)}</div>
        </div>
        <div>
          <div className="text-[9px] text-[#64748b] uppercase tracking-wide">5y / yr</div>
          <div className="tabular-nums text-[#cbd5e1]">{fiveYrAnn == null ? '—' : `${fiveYrAnn >= 0 ? '+' : ''}${fiveYrAnn.toFixed(1)}%`}</div>
        </div>
      </div>

      {mix && (
        <div className="px-3 pb-2" title="Share of the composite from each axis (α-scaled Q vs β-scaled V).">
          <div className="flex items-center justify-between text-[9px] text-[#64748b] uppercase tracking-wide mb-0.5">
            <span>Mix</span>
            <span>
              <span className="text-emerald-400">Q {Math.round(mix.qShare * 100)}%</span>
              {' · '}
              <span className="text-sky-400">V {Math.round(mix.vShare * 100)}%</span>
            </span>
          </div>
          <div className="flex h-1.5 rounded-sm overflow-hidden bg-[#1e293b]">
            <div className="bg-emerald-500" style={{ width: `${mix.qShare * 100}%` }} />
            <div className="bg-sky-500" style={{ width: `${mix.vShare * 100}%` }} />
          </div>
        </div>
      )}

      <div className="px-3 pb-2 grid grid-cols-2 gap-2 text-[10px]">
        <AxisStat label="Quality" z={Q} pct={selfPctile?.q?.percentile} regime={selfPctile?.q?.regime} color="emerald" />
        <AxisStat label="Valuation" z={V} pct={selfPctile?.v?.percentile} regime={selfPctile?.v?.regime} color="sky" />
      </div>

      {(target?.analystTarget != null || target?.fiveYearTarget != null) && (
        <div className="px-3 pb-2 flex items-baseline gap-3 flex-wrap text-[10px] border-t border-[#1e293b]/60 pt-1.5">
          {target?.fiveYearTarget != null && px != null && (
            <span className="text-[#94a3b8]">
              <span className="text-[9px] text-[#64748b] uppercase tracking-wide mr-1">5y target</span>
              <span className="tabular-nums text-[#cbd5e1]">${target.fiveYearTarget.toFixed(0)}</span>
              {fiveYrAnn != null && (
                <span className="ml-1 tabular-nums" style={{ color: fiveYrAnn >= 8 ? '#22c55e' : fiveYrAnn >= 0 ? '#94a3b8' : '#ef4444' }}>
                  ({fiveYrAnn >= 0 ? '+' : ''}{fiveYrAnn.toFixed(1)}%/yr)
                </span>
              )}
              {target.asymmetry != null && (
                <span className="ml-2 text-[9px] text-[#64748b]">asym {target.asymmetry.toFixed(1)}:1</span>
              )}
            </span>
          )}
          {target?.analystTarget != null && (
            <span className="text-[#94a3b8] ml-auto">
              <span className="text-[9px] text-[#64748b] uppercase tracking-wide mr-1">Street</span>
              <span className="tabular-nums text-[#cbd5e1]">${target.analystTarget.toFixed(0)}</span>
              {streetUpside != null && (
                <span className="ml-1 tabular-nums" style={{ color: streetUpside >= 0 ? '#94a3b8' : '#ef4444' }}>
                  ({streetUpside >= 0 ? '+' : ''}{streetUpside.toFixed(0)}%)
                </span>
              )}
              {target.analystCount != null && target.analystCount > 0 && (
                <span className="ml-2 text-[9px] text-[#64748b]">{target.analystCount} analysts</span>
              )}
            </span>
          )}
        </div>
      )}

      {isOpen && (
        <div className="px-3 pb-3 pt-2 border-t border-[#1e293b] text-[11px] text-[#cbd5e1] leading-relaxed space-y-3">
          {pillars && (
            <div>
              <div className="text-[9px] text-[#64748b] uppercase tracking-wide mb-1">Pillar Breakdown</div>
              <div className="space-y-1">
                {pillarRows.map(row => (
                  <PillarBar key={row.key} label={row.label} value={pillars[row.key] ?? null} weight={row.weight} />
                ))}
              </div>
            </div>
          )}

          {topFactors.length > 0 && (
            <div>
              <div className="text-[9px] text-[#64748b] uppercase tracking-wide mb-1">Top Contributing Factors (peer percentile)</div>
              <div className="grid grid-cols-1 gap-0.5">
                {topFactors.map(f => (
                  <div key={f.key} className="flex items-baseline justify-between">
                    <span className="text-[#cbd5e1]">{factorLabel(f.key)}</span>
                    <span className="tabular-nums text-emerald-300 font-mono">{((f.value as number) * 100).toFixed(0)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ThesisProse
            sym={sym}
            Q={Q}
            V={V}
            C={C}
            mix={mix}
            selfPctile={selfPctile}
            d20={d20}
            alphaStreak={vel?.alphaStreak ?? null}
            fiveYrAnn={fiveYrAnn}
            streetUpside={streetUpside}
            rating={target?.analystRating ?? null}
            asymmetry={target?.asymmetry ?? null}
            hedgeActive={!!hedge}
            tierLabel={tier.label}
            topFactorLabels={topFactors.slice(0, 3).map(f => factorLabel(f.key))}
          />

          <div className="text-[10px] text-[#94a3b8] italic border-t border-[#1e293b]/60 pt-2">
            {actionPrompt ?? 'Exit trigger: composite drops below 1.5 (BUY tier) or quality falls below zero at month-end rebalance.'}
          </div>
        </div>
      )}
    </div>
  );
}
